import React, { useRef, useCallback, useState, useEffect } from 'react';
import Webcam from 'react-webcam';
import logo from './logo.png';
import './App.css';

function App() {
  const [mode, setMode] = useState('upload');
  const [dark, setDark] = useState(false);
  const webcamRef = useRef(null);
  const [imgSrc, setImgSrc] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Toggle dark mode on body
  useEffect(() => {
    document.body.classList.toggle('dark', dark);
  }, [dark]);

  // Capture from camera
  const capture = useCallback(() => {
    const image = webcamRef.current.getScreenshot();
    if (image) {
      setImgSrc(image);
      analyzeImage(image);
    }
  }, [webcamRef]);

  // Handle file upload
  const handleFileChange = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setImgSrc(dataUrl);
      analyzeImage(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  // Core analysis call
  async function analyzeImage(dataUrl) {
    setLoading(true);
    setAnalysis(null);
    try {
      const blob = await fetch(dataUrl).then(r => r.blob());
      const file = new File([blob], 'upload.jpg', { type: blob.type });
      const form = new FormData();
      form.append('image', file);
      form.append('userId', 'guest');

      const res = await fetch('/api/analyze', { method: 'POST', body: form });
      const payload = await res.json();
      if (!res.ok) {
        alert(`❌ Server Error:\n${payload.error}\n\n${(payload.stack || []).join('\n')}`);
        return;
      }
      setAnalysis(payload.analysis);
      setChatHistory([]);
    } catch (err) {
      alert(`🌐 Network error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  // Send chat follow-up
  async function sendMessage() {
    if (!newMessage.trim() || !analysis) return;
    const userMsg = { role: 'user', content: newMessage };
    const history = [...chatHistory, userMsg];
    setChatHistory(history);
    setNewMessage('');

    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ analysis, history, message: newMessage })
      });
      const payload = await res.json();
      if (!res.ok) {
        alert(`❌ Server Error:\n${payload.error}\n\n${(payload.stack || []).join('\n')}`);
        return;
      }
      setChatHistory(h => [...h, { role: 'assistant', content: payload.reply }]);
    } catch (err) {
      alert(`🌐 Network error: ${err.message}`);
    }
  }

  return (
    <div className="app">
      {/* Loading bar */}
      {loading && <div className="loading-bar" />}

      <header className="header">
        <img src={logo} alt="Bloom Lingo" className="logo" />
        <div className="header-content">
          <h1>🌱 Bloom Lingo</h1>
          <button onClick={() => setDark(!dark)} className="toggle-btn">
            {dark ? '☀️ Light Mode' : '🌙 Dark Mode'}
          </button>
        </div>
      </header>

      <div className="controls">
        <button
          className={mode === 'upload' ? 'tab active' : 'tab'}
          onClick={() => setMode('upload')}
        >
          📂 Upload
        </button>
        <button
          className={mode === 'camera' ? 'tab active' : 'tab'}
          onClick={() => setMode('camera')}
        >
          📷 Camera
        </button>
      </div>

      {mode === 'upload' && (
        <input
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="file-input"
        />
      )}

      {mode === 'camera' && (
        <div className="camera-container">
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            width={320}
            height={240}
            className="webcam-view"
          />
          <button onClick={capture} className="capture-btn">
            📸 Capture
          </button>
        </div>
      )}

      {imgSrc && <img src={imgSrc} alt="Preview" className="preview" />}

      {!analysis && !loading && imgSrc && (
        <div className="skeleton-card">
          <div className="skeleton-line title" />
          <div className="skeleton-line" />
          <div className="skeleton-line" />
          <div className="skeleton-line" />
        </div>
      )}

      {analysis && (
        <div className="result card">
          <h2>✅ {analysis.name}</h2>
          <p>{analysis.description}</p>
          <div className="details">
            <span>🌡️ {analysis.temperature}</span>
            <span>🍂 {analysis.season}</span>
            <span>💧 {analysis.watering}</span>
            <span>☀️ {analysis.light}</span>
            <span>🦠 {analysis.disease}</span>
            <span>💊 {analysis.treatment}</span>
          </div>
        </div>
      )}

      {analysis && (
        <div className="chat card">
          <h3>💬 Chat about your plant</h3>
          <div className="chat-history">
            {chatHistory.map((m, i) => (
              <div key={i} className={`msg ${m.role === 'user' ? 'user' : 'bot'}`}>
                {m.content}
              </div>
            ))}
          </div>
          <div className="chat-input">
            <input
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              placeholder="Ask more..."
            />
            <button onClick={sendMessage} className="send-btn">
              ➡️ Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
